import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';

import { Alert, Flex, IconCheckmarkCircle, marginMixin, spaceMixin, Tag, Text } from '@zillow/constellation';
import DocumentController from './IncomeDocuments/DocumentController';
import DocumentsExpiredAlert from './DocumentsExpiredAlert';
import VoucherInformation from './VoucherInformation';
import styled from 'styled-components';
import FormatLogic from '../../utils/formatLogicUtil';

export const DIRECT_PAYMENT_BANNER =
  'This approved voucher amount will be paid directly by the local housing agency each month.';

const StyledAlert = styled(Alert)`
  ${marginMixin}
`; // TODO(CDS-958) - Alert component should support this natively

const CurrentIncomeContainer = styled(Flex)`
  &:not(:last-child) {
    margin-bottom: ${spaceMixin('lg')};
  }
`;

export default function CurrentIncome({ currentIncome, isVoucherEnabled, onOpenFileViewer, renderDocumentViewer }) {
  const {
    id,
    title,
    amount,
    note,
    type,
    documents = [],
    areDocumentsExpired,
    isVerified = false,
    verification = {},
  } = currentIncome;
  const isVoucher = type === 'voucher';
  const showVoucher = isVoucher && isVoucherEnabled;
  const jobTitle =
    type === 'employment' ? `${title} at ${currentIncome.employer.organization || currentIncome.employer.name}` : title;

  const startDate = FormatLogic.monthDayYearShort(verification?.startDate);
  const verifiedDate = FormatLogic.monthDayYearShort(verification?.verifiedDate);

  return (
    <CurrentIncomeContainer
      id="current-income"
      display="flex"
      flexDirection="column"
      data-test="current-income-container"
    >
      {showVoucher && <StyledAlert appearance="info" body={DIRECT_PAYMENT_BANNER} marginBottom="xs" />}
      {amount !== null && (
        <Flex display="flex" flexDirection={{ default: 'row', sm_lte: 'column' }}>
          <span>
            <Text fontType="h4">{numeral(amount).format('$0,0')}</Text>
            <Text fontType="h6" aria-label="per month">
              /mo
            </Text>
          </span>
          {!isVoucher && isVerified && (
            <span>
              <Tag appearance="success" marginLeft={{ md: 'sm' }}>
                <Flex display="flex" gap={0.5} alignItems="center">
                  <IconCheckmarkCircle size="xs" />
                  AMOUNT & SOURCE VERIFIED
                </Flex>
              </Tag>
            </span>
          )}
        </Flex>
      )}
      <Text id="job-title" fontType="h6" fontColor="textMedium" marginTop={0.5}>
        {jobTitle}
      </Text>
      {showVoucher && (
        <>
          <Text>Tenant-based voucher</Text>
          <VoucherInformation incomeSourceId={id} />
        </>
      )}
      {!isVoucher && isVerified && (
        <Text fontType="bodySmall" fontColor="textMedium">
          Regular bank deposits since {startDate} (Verified on {verifiedDate})
        </Text>
      )}
      {note && (
        <Text id="income-source-note" marginTop={0.5}>
          "{note}"
        </Text>
      )}
      {areDocumentsExpired && <DocumentsExpiredAlert documents={documents} />}
      {Boolean(documents.length) && (
        <DocumentController
          documents={documents}
          incomeSourceId={id}
          onOpenFileViewer={onOpenFileViewer}
          renderDocumentViewer={renderDocumentViewer}
        />
      )}
    </CurrentIncomeContainer>
  );
}

CurrentIncome.propTypes = {
  currentIncome: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    // ALW graph uses number, ZG graph uses string
    amount: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
    type: PropTypes.string.isRequired,
    note: PropTypes.string,
    employer: PropTypes.shape({
      // ALW graph uses name, ZG graph uses organization
      name: PropTypes.string,
      organization: PropTypes.string,
    }),
    documents: PropTypes.arrayOf(
      PropTypes.shape({
        documentId: PropTypes.string,
        fileName: PropTypes.string,
        previewUrl: PropTypes.string,
        expirationDateUtc: PropTypes.number,
      }),
    ),
    areDocumentsExpired: PropTypes.bool,
    isVerified: PropTypes.bool,
    verification: PropTypes.shape({
      vendor: PropTypes.string,
      startDate: PropTypes.number,
      endDate: PropTypes.number,
    }),
  }).isRequired,
  isVoucherEnabled: PropTypes.bool,
  renderDocumentViewer: PropTypes.func,
  onOpenFileViewer: PropTypes.func,
};

CurrentIncome.defaultProps = {
  isVoucherEnabled: true,
  renderDocumentViewer: null,
  onOpenFileViewer: null,
};
